import React, { useMemo, useRef, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import {
  ClickAwayListener,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Typography,
} from '@mui/material';
import Popper from 'components/common/popper/Popper';
import Edit2LineIcon from 'remixicon-react/Edit2LineIcon';
import IconButton from '@mui/material/IconButton';
import MoreFillIcon from 'remixicon-react/MoreFillIcon';
import GearIcon from 'remixicon-react/Settings5LineIcon';
import SelectMenu from 'components/common/menu/SelectMenu';
import useUserData, { UserType } from 'hooks/useUserData';
import FlagLineIcon from 'remixicon-react/FlagLineIcon';
import LogoutBoxRLineIcon from 'remixicon-react/LogoutBoxRLineIcon';
import NotificationLineIcon from 'remixicon-react/NotificationLineIcon';
import TermsAndConditionsIcon from 'remixicon-react/ArticleLineIcon';
import PrivacyIcon from 'remixicon-react/ShieldUserLineIcon';
import useReportModalStore from 'store/useReportModalStore';
import EditProfileModal from '../EditProfile/EditProfileModal';
import ProfileAvatar from './ProfileAvatar';
import ProfileMetaInfo from './ProfileMetaInfo';
import NotificationSettingsModal from 'components/modals/notificationSettingsModal/NotificationSettingsModal';
import { useLocation, useNavigate } from 'react-router-dom';
import { handlePageScrollPosition } from 'utility/helpers';
import { MenuItemType } from 'components/drawer/RenderMenu';
import { PRIVACY, TERMS_CONDITIONS } from 'constants/routes';

type ProfileDetailsProps = {
  user: UserType;
  isLoggedInUser: boolean;
};

const ProfileDetails: React.FC<ProfileDetailsProps> = ({ user, isLoggedInUser }) => {
  const { state } = useLocation();
  const theme = useTheme();
  const [openEditProfile, setOpenEditProfile] = useState(false);
  const openReport = useReportModalStore((state) => state.openReport);
  const [openNotificationSettingsModal, setOpenNotificationSettingsModal] = useState(false);
  const navigate = useNavigate();
  const { logout } = useUserData({
    shouldFetchCompanyDetails: false,
    shouldFetchUserDetails: false,
    selectedCompanyId: state?.selectedCompanyId || '',
  });
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleLogout = () => {
    handlePageScrollPosition('all', 0, 0, 'reset');
    logout();
    navigate('/login');
  };
  const menu: MenuItemType[] = useMemo(
    () => [
      {
        title: 'Report',
        icon: <FlagLineIcon size={17} />,
        onClick: () =>
          openReport({
            id: user.id,
            text: user.username,
            reportObject: 'User',
          }),
      },
    ],
    [user],
  );

  return (
    <ProfileDetailsContainer>
      <Box display="flex" justifyContent="space-between">
        <Box display="flex">
          <Box mr={3}>
            <ProfileAvatar
              user={user}
              isLoggedInUser={isLoggedInUser}
              showUploadIcon={!user?.profileImageURL}
            />
          </Box>
          <Box flex={1}>
            <ProfileMetaInfo
              user={user}
              isLoggedInUser={isLoggedInUser}
              setOpenEditProfile={setOpenEditProfile}
            />
          </Box>
        </Box>
        <Box>
          <Box display="flex" alignItems="center">
            {isLoggedInUser ? (
              <>
                <IconButton title="More Actions" onClick={handleClick}>
                  {isLoggedInUser ? (
                    <GearIcon color={theme.palette.custom.navy} />
                  ) : (
                    <MoreFillIcon color={theme.palette.custom.lightBlack} />
                  )}
                </IconButton>
                <Popper
                  open={open}
                  anchor={anchorEl}
                  modifiers={[
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 10],
                      },
                    },
                  ]}
                >
                  <ClickAwayListener onClickAway={handleClose}>
                    <Container>
                      <MenuList sx={{ padding: 0 }}>
                        <MenuItem
                          sx={{
                            padding: '10px 16px',
                            '&:hover': {
                              backgroundColor: theme.palette.custom.background,
                              borderTopLeftRadius: 8,
                              borderTopRightRadius: 8,
                            },
                          }}
                          onClick={() => {
                            handleClose();
                            setOpenNotificationSettingsModal(true);
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 'fit-content !important', mr: 1 }}>
                            <NotificationLineIcon size={18} />
                          </ListItemIcon>
                          <ListItemText>
                            <Typography sx={{ fontSize: 14 }}>Notification Settings</Typography>
                          </ListItemText>
                        </MenuItem>

                        <MenuItem
                          sx={{
                            padding: '10px 16px',
                            borderTop: `1px solid ${theme.palette.custom.blueChalk}`,
                            '&:hover': {
                              backgroundColor: theme.palette.custom.background,
                            },
                          }}
                          onClick={() => window.open(TERMS_CONDITIONS, '_blank')}
                        >
                          <ListItemIcon sx={{ minWidth: 'fit-content !important', mr: 1 }}>
                            <TermsAndConditionsIcon size={18} />
                          </ListItemIcon>
                          <ListItemText>
                            <Typography sx={{ fontSize: 14 }}>Terms & Conditions</Typography>
                          </ListItemText>
                        </MenuItem>
                        <MenuItem
                          sx={{
                            padding: '10px 16px',
                            '&:hover': {
                              backgroundColor: theme.palette.custom.background,
                              borderBottomLeftRadius: 8,
                              borderBottomRightRadius: 8,
                            },
                          }}
                          onClick={() => window.open(PRIVACY, '_blank')}
                        >
                          <ListItemIcon sx={{ minWidth: 'fit-content !important', mr: 1 }}>
                            <PrivacyIcon size={18} />
                          </ListItemIcon>
                          <ListItemText>
                            <Typography sx={{ fontSize: 14 }}>Privacy Policy</Typography>
                          </ListItemText>
                        </MenuItem>
                      </MenuList>
                    </Container>
                  </ClickAwayListener>
                </Popper>
              </>
            ) : (
              <SelectMenu menu={menu}>
                <IconButton title="More Actions">
                  <MoreFillIcon color={theme.palette.custom.lightBlack} />
                </IconButton>
              </SelectMenu>
            )}

            {isLoggedInUser && (
              <IconButton title="Sign Out" onClick={handleLogout}>
                <LogoutBoxRLineIcon size={19} color={theme.palette.custom.redWine} />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      {openEditProfile && (
        <EditProfileModal open={openEditProfile} setOpen={setOpenEditProfile} user={user} />
      )}

      {openNotificationSettingsModal && (
        <NotificationSettingsModal
          open={openNotificationSettingsModal}
          setOpen={setOpenNotificationSettingsModal}
        />
      )}
    </ProfileDetailsContainer>
  );
};

const ProfileDetailsContainer = styled(Paper, { label: 'ProfileDetailsContainer' })(
  ({ theme }) => ({
    position: 'relative',
    width: '100%',
    minHeight: 150,
    backgroundColor: theme.palette.custom.white,
    padding: 24,
    borderRadius: 8,
    boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
    marginBottom: 16,
  }),
);

const Container = styled(Box, { label: 'Container' })({
  minWidth: 200,
  // padding: '0 0 16px 0',
});

export default ProfileDetails;
